import React, { useState, useEffect, useContext } from "react";
import moment from "moment";
import { useQuery, useMutation, useApolloClient } from "react-apollo";
import { Field, Control, Icon, Tab, Button, Title, Tag } from "rbx";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import {
  useParams,
  useLocation,
  Route,
  Switch,
  useHistory
} from "react-router-dom";

import {
  SINGLE_VEHICLE_QUERY,
  UPDATE_VEHICLE_MUTATION,
  DELETE_VEHICLE_MUTATION,
  ALL_VEHICLE_INSPECTIONS_QUERY,
  ALL_VEHICLE_OUT_OF_SERVICES_VIEW_BY_PAGE_QUERY,
  ALL_VEHICLES_QUERY,
  ALL_VW_VEHICLE_ELD_READING_QUERY
} from "../../../graphql/Vehicles/Vehicles.graphql";
import { ALL_ITEM_TRANSACTIONS_VIEW_QUERY } from "../../../graphql/Items/ItemTransactions.graphql";
import { ITEM_TRANSACTION_COLUMNS } from "../../InventoryManagement/ItemTransactions/columns";
import { ALL_WORK_ORDER_LABORS_VIEW_QUERY } from "../../../graphql/WorkOrders/WorkOrderLabors.graphql";
import {
  WORK_ORDER_LABOR_VIEW_COLUMNS,
  WORK_ORDER_PARTS_VIEW_COLUMNS,
  VEHICLE_ELD_COLUMNS,
  VEHICLE_INSPECTION_COLUMNS
} from "./columns";
import { VEHICLE_OUT_OF_SERVICES_COLUMNS } from "../VehicleOutOfServices/columns";

import { ALL_WORK_ORDER_VIEW_QUERY } from "../../../graphql/WorkOrders/WorkOrders.graphql";
import { ALL_WORK_ORDER_PARTS_VIEW_QUERY } from "../../../graphql/WorkOrders/WorkOrderParts.graphql";
import { ModalContext } from "../../../components/ModalProvider";
import Profile from "./routes/Profile";
import ElectronicLogDevice from "./routes/ElectronicLogDevice";
import Inspections from "./routes/Inspections";
import LaborHistory from "./routes/LaborHistory";
import PartsHistory from "./routes/PartsHistory";
import {
  downloadCSV,
  getFilterState,
  saveFilterState,
  getUTCFormattedDate,
  ourToast as toast
} from "../../../utils";
import { useAuthorization } from "../../../components/Authorization";
import VehiclePartsFilter from "./VehiclePartsFilter";
import VehicleLaborsFilter from "./VehicleLaborsFilter";
import Loading from "../../../components/Loading";
import DataTable from "../../../components/DataTable";
import { VehicleOutOfServiceModal } from "../../../components/VehicleOutOfService";
import VehicleELDFilter from "./VehicleELDFilter";
import VehicleInspectionFilter from "./VehicleInspectionFilter";

const INITIAL_STATE = {
  VehicleNumber: "",
  Description: null,
  VehicleTypeRecIdRef: null,
  StatusRecIdRef: null,
  CategoryRecIdRef: null,
  CompanyOwned: false,
  Year: null,
  Make: null,
  Model: null,
  SerialNumber: null,
  Engine: null,
  Axles: null,
  TransmissionRecIdRef: null,
  RearRecIdRef: null,
  BodyRecIdRef: null,
  NumDieselTanks: null,
  TireSizeRecIdRef: null,
  WheelSizeRecIdRef: null,
  MaxCold: null,
  MaxWarm: null,
  PlateNumber: null,
  RegistrationNumber: null,
  RegistrationState: null,
  RegistrationExpirationDate: null,
  OverWeightPermit: null,
  OverWeightPermitExpirationDate: "",
  Unladen: null,
  Payload: null,
  TrycockNet: null,
  CompanyId: null,
  RecId: null,
  Odometer: null,
  ELDVehicleId: null
};

function VehiclePage() {
  const { id } = useParams();
  const location = useLocation();
  const client = useApolloClient();

  const [edited, setEdited] = useState(false);

  const AdminAuth = useAuthorization(["ADMIN"]);
  const FleetAuth = useAuthorization(["FLEET"]);
  const MechAuth = useAuthorization(["MECH"]);
  const DispatcherAuth = useAuthorization(["DISP"]);

  const isMechanic = MechAuth && !AdminAuth && !FleetAuth;
  const isDispatcher = DispatcherAuth && !AdminAuth && !FleetAuth;

  const INITIAL_PARTS_FILTER_STATE = getFilterState("VEHICLE_PARTS_FILTERS");
  const INITIAL_LABORS_FILTER_STATE = getFilterState("VEHICLE_LABORS_FILTERS");
  const INITIAL_ELD_FILTERS_STATE = getFilterState("VEHICLE_ELD_FILTERS");
  const INITIAL_INSPECTIONS_FILTERS_STATE = getFilterState(
    "VEHICLE_INSPECTIONS_FILTERS"
  );
  const [laborFilters, setLaborFilters] = useState(null);
  const [partFilters, setPartFilters] = useState(null);
  const [ELDFilters, setELDFilters] = useState(null);
  const [InspectionsFilters, setInspectionsFilters] = useState(null);

  const [isLoading, setLoading] = useState(false);
  const { setModalOpen } = useContext(ModalContext);
  const history = useHistory();

  const vehicleData = useQuery(SINGLE_VEHICLE_QUERY, {
    variables: { where: { RecId: parseInt(id) } }
  });

  const [updateVehicle, { loading }] = useMutation(UPDATE_VEHICLE_MUTATION, {
    refetchQueries: [
      "SINGLE_VEHICLE_QUERY",
      "ALL_VEHICLES_QUERY",
      "ALL_VEHICLES_VIEW_BY_PAGE_QUERY"
    ]
  });

  const [inputs, setInputs] = useState({ ...INITIAL_STATE });
  const [editing, setEditing] = useState(false);

  useEffect(() => {
    if (
      !inputs.RecId &&
      !vehicleData.loading &&
      vehicleData.data &&
      vehicleData.data.Vehicle
    ) {
      setInputs(vehicleData.data.Vehicle);
    }
    if (!partFilters) {
      setPartFilters(INITIAL_PARTS_FILTER_STATE);
    }
    if (!laborFilters) {
      setLaborFilters(INITIAL_LABORS_FILTER_STATE);
    }
    if (!ELDFilters) {
      setELDFilters(INITIAL_ELD_FILTERS_STATE);
    }
    if (!InspectionsFilters) {
      setInspectionsFilters(INITIAL_INSPECTIONS_FILTERS_STATE);
    }
  }, [
    INITIAL_LABORS_FILTER_STATE,
    INITIAL_PARTS_FILTER_STATE,
    INITIAL_ELD_FILTERS_STATE,
    INITIAL_INSPECTIONS_FILTERS_STATE,
    inputs.RecId,
    laborFilters,
    loading,
    partFilters,
    ELDFilters,
    InspectionsFilters,
    vehicleData.data,
    vehicleData.loading
  ]);

  const handleChange = e => {
    setEdited(true);
    const value =
      e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setInputs({ ...inputs, [e.target.name]: value });
  };

  const toggleEditing = () => (isMechanic ? null : setEditing(!editing));

  const handleSave = async goTo => {
    try {
      const obj = Object.keys(INITIAL_STATE).reduce(
        (acc, curr) =>
          inputs[curr] !== null ? { ...acc, [curr]: inputs[curr] } : acc,
        {}
      );
      const data = {
        ...obj,
        VehicleTypeRecIdRef: parseInt(inputs.VehicleTypeRecIdRef),
        StatusRecIdRef: parseInt(inputs.StatusRecIdRef),
        CategoryRecIdRef: parseInt(inputs.CategoryRecIdRef),
        Axles: parseInt(inputs.Axles),
        TransmissionRecIdRef: parseInt(inputs.TransmissionRecIdRef),
        RearRecIdRef: parseInt(inputs.RearRecIdRef),
        BodyRecIdRef: parseInt(inputs.BodyRecIdRef),
        NumDieselTanks: parseInt(inputs.NumDieselTanks),
        TireSizeRecIdRef: parseInt(inputs.TireSizeRecIdRef),
        MaxWarm: parseInt(inputs.MaxWarm),
        MaxCold: parseInt(inputs.MaxCold),
        Unladen: parseInt(inputs.Unladen),
        Payload: parseInt(inputs.Payload),
        TrycockNet: parseInt(inputs.TrycockNet),
        Year: parseInt(inputs.Year),
        RegistrationExpirationDate: getUTCFormattedDate(
          inputs.RegistrationExpirationDate
        ),
        OverWeightPermitExpirationDate: inputs.OverWeightPermitExpirationDate
          ? getUTCFormattedDate(inputs.OverWeightPermitExpirationDate)
          : null,
        Odometer: parseFloat(inputs.Odometer)
      };
      const vehicleExists = await client.query({
        query: ALL_VEHICLES_QUERY,
        variables: {
          where: { VehicleNumber: data.VehicleNumber, RecId_not: parseInt(id) },
          first: 1
        }
      });
      if (
        vehicleExists &&
        vehicleExists.data &&
        Array.isArray(vehicleExists.data.Vehicles) &&
        vehicleExists.data.Vehicles.length > 0
      ) {
        toast.error("This vehicle number is being used by another vehicle.");
      } else {
        await updateVehicle({
          variables: {
            data,
            where: { RecId: parseInt(id) }
          },
          refetchQueries: ["ALL_VEHICLES_QUERY"]
        });
        toast.success("Vehicle updated successfully.");
        if (goTo) {
          history.push(goTo);
        }
        setEdited(false);
      }
    } catch (err) {
      console.log(err.message);
    }
  };

  const handleDelete = async () => {
    setLoading(true);
    const woRes = await client.query({
      query: ALL_WORK_ORDER_VIEW_QUERY,
      variables: {
        where: {
          VehicleRecIdRef: parseInt(id)
        }
      }
    });
    const viRes = await client.query({
      query: ALL_VEHICLE_INSPECTIONS_QUERY,
      variables: {
        where: {
          VehicleRecIdRef: parseInt(id)
        }
      }
    });
    const itRes = await client.query({
      query: ALL_ITEM_TRANSACTIONS_VIEW_QUERY,
      variables: {
        where: {
          VehicleRecIdRef: parseInt(id)
        }
      }
    });
    if (
      !woRes.loading &&
      !viRes.loading &&
      !itRes.loading &&
      woRes.data &&
      viRes.data &&
      itRes.data &&
      Array.isArray(woRes.data.VW_WorkOrders) &&
      Array.isArray(viRes.data.VehicleInspections) &&
      Array.isArray(itRes.data.VW_ItemTransactions) &&
      (woRes.data.VW_WorkOrders.length > 0 ||
        (viRes.data.VehicleInspections.length > 0 &&
          itRes.data.VW_ItemTransactions.length > 0))
    ) {
      setLoading(false);
      toast.error("This vehicle can not be deleted");
    } else {
      await client.mutate({
        mutation: DELETE_VEHICLE_MUTATION,
        variables: {
          where: {
            RecId: parseInt(id)
          }
        },
        refetchQueries: ["ALL_VEHICLES_QUERY"]
      });
      setLoading(false);
      history.push("/fleet/vehicles");
    }
  };

  const handleSetPartFilters = filters => {
    saveFilterState("VEHICLE_PARTS_FILTERS", filters);
    setPartFilters({ ...filters, VehicleRecIdRef: parseInt(id) });
  };

  const handleSetLaborFilters = filters => {
    saveFilterState("VEHICLE_LABORS_FILTERS", filters);
    setLaborFilters({ ...filters, VehicleRecIdRef: parseInt(id) });
  };

  const handleSetELDFilters = filters => {
    const where = { ...filters };
    if (where.ELDDate_gte) {
      where.ELDDate_gte = moment(where.ELDDate_gte)
        .startOf("day")
        .toISOString();
    }
    if (where.ELDDate_lte) {
      where.ELDDate_lte = moment(where.ELDDate_lte)
        .endOf("day")
        .toISOString();
    }
    saveFilterState("VEHICLE_ELD_FILTERS", where);
    setELDFilters({ ...where });
  };

  const handleSetInspectionsFilters = filters => {
    // Clearing filters if is empty and avoid null response from query
    Object.keys(filters).forEach(
      key =>
        (!filters[key] || filters[key] === 0 || filters[key] === "") &&
        delete filters[key]
    );
    const where = { ...filters };
    if (where.InspectionDate_gte) {
      where.InspectionDate_gte = moment(where.InspectionDate_gte)
        .startOf("day")
        .toISOString();
    }
    if (where.InspectionDate_lte) {
      where.InspectionDate_lte = moment(where.InspectionDate_lte)
        .endOf("day")
        .toISOString();
    }
    saveFilterState("VEHICLE_INSPECTIONS_FILTERS", where);
    setInspectionsFilters({ ...where });
  };

  const getActionButtons = () => {
    if (location.pathname.includes("ELD")) {
      return (
        <Field kind="group">
          <Control>
            <Button
              color="danger"
              size="small"
              onClick={() =>
                setModalOpen(
                  true,
                  <VehicleELDFilter
                    filters={{ ...ELDFilters, VehicleRecIdRef: parseInt(id) }}
                    id={id}
                    onClose={() => setModalOpen(false)}
                    onFilter={handleSetELDFilters}
                  />
                )
              }
            >
              <Icon>
                <FontAwesomeIcon icon="filter" />
              </Icon>
              <span>Filter</span>
            </Button>
          </Control>
          <Control>
            <Button
              color="primary"
              size="small"
              onClick={() =>
                downloadCSV(
                  client,
                  {
                    query: ALL_VW_VEHICLE_ELD_READING_QUERY,
                    where: { ...ELDFilters, VehicleRecIdRef: parseInt(id) }
                  },
                  "VW_VehicleELDReadings",
                  VEHICLE_ELD_COLUMNS
                )
              }
            >
              <Icon>
                <FontAwesomeIcon icon="file-export" />
              </Icon>
              <span>Export</span>
            </Button>
          </Control>
        </Field>
      );
    }
    if (location.pathname.includes("inspections")) {
      return (
        <Field kind="group">
          <Control>
            <Button
              color="danger"
              size="small"
              onClick={() =>
                setModalOpen(
                  true,
                  <VehicleInspectionFilter
                    filters={InspectionsFilters}
                    id={id}
                    onClose={() => setModalOpen(false)}
                    onFilter={handleSetInspectionsFilters}
                  />
                )
              }
            >
              <Icon>
                <FontAwesomeIcon icon="filter" />
              </Icon>
              <span>Filter</span>
            </Button>
          </Control>
          <Control>
            <Button
              color="primary"
              size="small"
              onClick={() =>
                downloadCSV(
                  client,
                  {
                    query: ALL_VEHICLE_INSPECTIONS_QUERY,
                    where: {
                      ...InspectionsFilters,
                      VehicleRecIdRef: parseInt(id)
                    }
                  },
                  "VehicleInspections",
                  VEHICLE_INSPECTION_COLUMNS
                )
              }
            >
              <Icon>
                <FontAwesomeIcon icon="file-export" />
              </Icon>
              <span>Export</span>
            </Button>
          </Control>
        </Field>
      );
    }
    if (location.pathname.includes("maintenance-history")) {
      return (
        <Field kind="group">
          <Control>
            <Button
              color="primary"
              size="small"
              onClick={() =>
                downloadCSV(
                  client,
                  {
                    query: ALL_ITEM_TRANSACTIONS_VIEW_QUERY,
                    where: { VehicleRecIdRef: parseInt(id) }
                  },
                  "VW_ItemTransactions",
                  ITEM_TRANSACTION_COLUMNS
                )
              }
            >
              <Icon>
                <FontAwesomeIcon icon="file-export" />
              </Icon>
              <span>Export</span>
            </Button>
          </Control>
        </Field>
      );
    }
    if (location.pathname.includes("labor")) {
      return (
        <Field kind="group">
          <Control>
            <Button
              color="danger"
              size="small"
              onClick={() =>
                setModalOpen(
                  true,
                  <VehicleLaborsFilter
                    filters={laborFilters}
                    id={id}
                    onClose={() => setModalOpen(false)}
                    onFilter={handleSetLaborFilters}
                  />
                )
              }
            >
              <Icon>
                <FontAwesomeIcon icon="filter" />
              </Icon>
              <span>Filter</span>
            </Button>
          </Control>
          <Control>
            <Button
              color="primary"
              size="small"
              onClick={() =>
                downloadCSV(
                  client,
                  {
                    query: ALL_WORK_ORDER_LABORS_VIEW_QUERY,
                    where: { VehicleRecIdRef: parseInt(id) }
                  },
                  "VW_WorkOrderLabors",
                  WORK_ORDER_LABOR_VIEW_COLUMNS
                )
              }
            >
              <Icon>
                <FontAwesomeIcon icon="file-export" />
              </Icon>
              <span>Export</span>
            </Button>
          </Control>
        </Field>
      );
    }
    if (location.pathname.includes("parts")) {
      return (
        <Field kind="group">
          <Control>
            <Button
              color="danger"
              size="small"
              onClick={() =>
                setModalOpen(
                  true,
                  <VehiclePartsFilter
                    filters={partFilters}
                    id={id}
                    onClose={() => setModalOpen(false)}
                    onFilter={handleSetPartFilters}
                  />
                )
              }
            >
              <Icon>
                <FontAwesomeIcon icon="filter" />
              </Icon>
              <span>Filter</span>
            </Button>
          </Control>
          <Control>
            <Button
              color="primary"
              size="small"
              onClick={() =>
                downloadCSV(
                  client,
                  {
                    query: ALL_WORK_ORDER_PARTS_VIEW_QUERY,
                    where: { VehicleRecIdRef: parseInt(id) }
                  },
                  "VW_WorkOrderParts",
                  WORK_ORDER_PARTS_VIEW_COLUMNS(FleetAuth || AdminAuth)
                )
              }
            >
              <Icon>
                <FontAwesomeIcon icon="file-export" />
              </Icon>
              <span>Export</span>
            </Button>
          </Control>
        </Field>
      );
    }
    return (
      <Field kind="group">
        <Control>
          <Button
            color="primary"
            disabled={editing || isMechanic || isDispatcher}
            size="small"
            onClick={toggleEditing}
          >
            <Icon>
              <FontAwesomeIcon icon="edit" />
            </Icon>
            <span>Edit</span>
          </Button>
        </Control>
        {vehicleData?.data?.Vehicle && (
          <Control>
            <Button
              color="danger"
              disabled={!editing || isMechanic}
              size="small"
              onClick={handleDelete}
            >
              <Icon>
                <FontAwesomeIcon icon="trash" />
              </Icon>
              <span>Delete</span>
            </Button>
          </Control>
        )}
        <Control>
          <Button
            color="success"
            disabled={!editing || isMechanic}
            size="small"
            onClick={() => handleSave(false)}
          >
            <Icon>
              <FontAwesomeIcon icon="save" />
            </Icon>
            <span>Save</span>
          </Button>
        </Control>
      </Field>
    );
  };

  const changeTab = tab => () => {
    if (edited && location.pathname.includes("profile")) {
      handleSave(`/fleet/vehicles/${vehicleData?.data?.Vehicle?.RecId}/${tab}`);
    } else {
      history.push(
        `/fleet/vehicles/${vehicleData?.data?.Vehicle?.RecId}/${tab}`
      );
    }
  };

  if (loading || isLoading || vehicleData?.loading) return <Loading />;

  return (
    <div className="vehicle-page">
      <header className="page-head">
        <div className="page-head-start">
          <Title color="primary" size={1}>
            {vehicleData?.data?.Vehicle ? inputs?.VehicleNumber : "New Vehicle"}
          </Title>
          {vehicleData?.data?.Vehicle && (
            <div className="page-head-start--subheader">
              {vehicleData?.data?.Vehicle?.Status && (
                <Tag
                  color={
                    vehicleData?.data?.Vehicle?.Status ? "success" : "danger"
                  }
                >
                  {vehicleData?.data?.Vehicle?.Status?.CodeDescription?.toUpperCase()}
                </Tag>
              )}
              <span>{`${inputs?.Description?.substr(0, 16) || ""}`}</span>
            </div>
          )}
        </div>

        <div className="page-head-end">{getActionButtons()}</div>
      </header>
      <div className="page-head-tabs">
        <Tab.Group kind="boxed">
          <Tab
            active={location.pathname.includes("profile")}
            as={Button}
            className={isDispatcher ? "turn-off-hover" : ""}
            onClick={changeTab("profile")}
          >
            <span>Profile</span>
          </Tab>
          <Tab
            active={location.pathname.includes("ELD")}
            as={Button}
            onClick={changeTab("ELD")}
          >
            <span>ELD Expenses</span>
          </Tab>
          {vehicleData?.data?.Vehicle && !isDispatcher && (
            <React.Fragment>
              <Tab
                active={location.pathname.includes("inspections")}
                as={Button}
                onClick={changeTab("inspections")}
              >
                <span>Inspections</span>
              </Tab>
              {(FleetAuth || AdminAuth || MechAuth) && (
                <React.Fragment>
                  <Tab
                    active={location.pathname.includes("labor")}
                    as={Button}
                    onClick={changeTab("labor")}
                  >
                    <span>Labor</span>
                  </Tab>
                  <Tab
                    active={location.pathname.includes("parts")}
                    as={Button}
                    onClick={changeTab("parts")}
                  >
                    <span>Parts</span>
                  </Tab>
                </React.Fragment>
              )}
              <Tab
                active={location.pathname.includes("o-o-s-history")}
                as={Button}
                onClick={changeTab("o-o-s-history")}
              >
                <span>OOS History</span>
              </Tab>
            </React.Fragment>
          )}
        </Tab.Group>
      </div>
      <Switch>
        <Route path="/fleet/vehicles/:id/profile">
          <Profile
            editing={editing}
            inputs={inputs}
            setModalOpen={setModalOpen}
            onChange={handleChange}
          />
        </Route>
        <Route path="/fleet/vehicles/:id/ELD">
          <ElectronicLogDevice
            editing={editing}
            filters={{ ...ELDFilters, VehicleRecIdRef: parseInt(id) }}
            inputs={inputs}
            onChange={handleChange}
          />
        </Route>
        <Route path="/fleet/vehicles/:id/inspections">
          <Inspections
            editing={editing}
            filters={InspectionsFilters}
            inputs={inputs}
            onChange={handleChange}
          />
        </Route>
        <Route path="/fleet/vehicles/:id/labor">
          <LaborHistory
            editing={editing}
            filters={laborFilters}
            inputs={inputs}
            onChange={handleChange}
          />
        </Route>
        <Route path="/fleet/vehicles/:id/parts">
          <PartsHistory
            Auth={FleetAuth || AdminAuth}
            editing={editing}
            filters={partFilters}
            inputs={inputs}
            onChange={handleChange}
          />
        </Route>
        <Route path="/fleet/vehicles/:id/o-o-s-history">
          <DataTable
            isPaginated
            columns={VEHICLE_OUT_OF_SERVICES_COLUMNS(null, true, true)}
            dataQuery={ALL_VEHICLE_OUT_OF_SERVICES_VIEW_BY_PAGE_QUERY}
            initialSortBy={[{ id: "CreatedDateTime", desc: true }]}
            name="VW_VehicleOutOfServicesByPage"
            where={{ VehicleRecIdRef: parseInt(id) }}
            onRowClick={({ RecId }) =>
              setModalOpen(
                true,
                <VehicleOutOfServiceModal
                  {...{
                    id: RecId,
                    onComplete: () => setModalOpen(false)
                  }}
                />
              )
            }
          />
        </Route>
      </Switch>
    </div>
  );
}
VehiclePage.propTypes = {};
export default VehiclePage;
